/**
 * Asignacion y fusion de categorias (seccion 6.4).
 * Puro y sin dependencias: se reutiliza en las Edge Functions.
 */

import {
  CATEGORY_RULES,
  OPEN_WEIGHT_REFERENCE,
  WEIGHT_CLASSES,
  WEIGHT_CLASS_ORDER,
} from '@/config/categories';
import type { GenderCategory, WeightClass } from '@/types/domain';

/** Tabla de pesos que aplica a cada categoria de sexo (OPEN usa la de referencia). */
export function weightTableFor(gender: GenderCategory) {
  return WEIGHT_CLASSES[gender === 'OPEN' ? OPEN_WEIGHT_REFERENCE : gender];
}

/**
 * Categoria de peso a partir del peso corporal declarado (RF-CAT-02).
 * Los limites son inclusivos: 67,0 kg es Ligero en masculino; 67,1 kg es Medio.
 */
export function assignWeightClass(bodyWeightKg: number, gender: GenderCategory): WeightClass {
  const table = weightTableFor(gender);
  for (const band of table) {
    if (band.maxKg === null) return band.code;
    if (bodyWeightKg <= band.maxKg) return band.code;
  }
  return 'HEAVY';
}

/** Rango legible de una categoria de peso, para ensenarlo en la interfaz. */
export function weightClassRange(
  weightClass: WeightClass,
  gender: GenderCategory,
): { minKg: number | null; maxKg: number | null } {
  const table = weightTableFor(gender);
  const index = table.findIndex((band) => band.code === weightClass);
  if (index === -1) return { minKg: null, maxKg: null };
  const previous = index > 0 ? table[index - 1] : undefined;
  const minKg = previous?.maxKg != null ? round1(previous.maxKg + 0.1) : null;
  return { minKg, maxKg: table[index].maxKg };
}

/** Clave estable de una categoria, usada en URLs y filtros (`m-medio`). */
export function categoryKey(gender: GenderCategory, weightClass: WeightClass): string {
  return `${gender.toLowerCase()}-${weightClass.toLowerCase()}`;
}

export function parseCategoryKey(
  key: string,
): { gender: GenderCategory; weightClass: WeightClass } | null {
  const [rawGender, ...rest] = key.split('-');
  const rawWeight = rest.join('-').toUpperCase();
  const gender = rawGender?.toUpperCase();
  if (gender !== 'M' && gender !== 'F' && gender !== 'OPEN') return null;
  if (!WEIGHT_CLASS_ORDER.includes(rawWeight as WeightClass)) return null;
  return { gender, weightClass: rawWeight as WeightClass };
}

// ---------------------------------------------------------------------------
// Fusion de categorias con pocos participantes (RF-CAT-06)
// ---------------------------------------------------------------------------

export interface CategoryCount {
  gender: GenderCategory;
  weightClass: WeightClass;
  participants: number;
}

export interface CategoryMerge {
  gender: GenderCategory;
  /** Categorias de peso que quedan agrupadas en una sola clasificacion. */
  merged: WeightClass[];
  /** Categoria que da nombre al grupo resultante. */
  resultingClass: WeightClass;
  participants: number;
}

/**
 * Fusiona hacia la categoria de peso contigua toda division con menos de
 * `MIN_PARTICIPANTS_PER_CATEGORY` participantes. La division por sexo nunca se
 * fusiona (RF-CAT-06): M, F y OPEN se resuelven por separado.
 *
 * Recorre de ligero a pesado arrastrando el grupo pequeno hacia arriba; si el
 * ultimo grupo se queda corto, se une al anterior.
 */
export function mergeSmallCategories(counts: CategoryCount[]): CategoryMerge[] {
  const minimum = CATEGORY_RULES.MIN_PARTICIPANTS_PER_CATEGORY;
  const byGender = new Map<GenderCategory, CategoryCount[]>();

  for (const count of counts) {
    const list = byGender.get(count.gender) ?? [];
    list.push(count);
    byGender.set(count.gender, list);
  }

  const result: CategoryMerge[] = [];

  for (const [gender, list] of byGender) {
    const ordered = WEIGHT_CLASS_ORDER.map(
      (weightClass) =>
        list.find((c) => c.weightClass === weightClass) ?? {
          gender,
          weightClass,
          participants: 0,
        },
    ).filter((c) => c.participants > 0 || list.some((l) => l.weightClass === c.weightClass));

    const groups: CategoryMerge[] = [];
    let pending: WeightClass[] = [];
    let pendingCount = 0;

    for (const entry of ordered) {
      pending.push(entry.weightClass);
      pendingCount += entry.participants;

      if (pendingCount >= minimum) {
        groups.push({
          gender,
          merged: [...pending],
          // El grupo toma el nombre de su categoria mas pesada.
          resultingClass: pending[pending.length - 1],
          participants: pendingCount,
        });
        pending = [];
        pendingCount = 0;
      }
    }

    // Resto por debajo del minimo: se une al ultimo grupo cerrado, o queda solo
    // si no habia ninguno (competicion entera con menos de 5 participantes).
    if (pending.length > 0) {
      const last = groups[groups.length - 1];
      if (last) {
        last.merged.push(...pending);
        last.resultingClass = pending[pending.length - 1];
        last.participants += pendingCount;
      } else {
        groups.push({
          gender,
          merged: [...pending],
          resultingClass: pending[pending.length - 1],
          participants: pendingCount,
        });
      }
    }

    result.push(...groups);
  }

  return result;
}

/** Grupo en el que compite una categoria concreta tras aplicar las fusiones. */
export function resolveMergedCategory(
  merges: CategoryMerge[],
  gender: GenderCategory,
  weightClass: WeightClass,
): CategoryMerge | undefined {
  return merges.find((m) => m.gender === gender && m.merged.includes(weightClass));
}

// ---------------------------------------------------------------------------
// Periodos de espera (RF-CAT-07 / RF-ONB-03)
// ---------------------------------------------------------------------------

export interface CooldownState {
  allowed: boolean;
  daysRemaining: number;
  nextChangeAt: Date | null;
}

function cooldown(lastChange: string | null, days: number, now: Date): CooldownState {
  if (!lastChange) return { allowed: true, daysRemaining: 0, nextChangeAt: null };
  const last = new Date(lastChange);
  if (Number.isNaN(last.getTime())) {
    return { allowed: true, daysRemaining: 0, nextChangeAt: null };
  }
  const next = new Date(last.getTime() + days * 24 * 60 * 60 * 1000);
  const remainingMs = next.getTime() - now.getTime();
  if (remainingMs <= 0) return { allowed: true, daysRemaining: 0, nextChangeAt: null };
  return {
    allowed: false,
    daysRemaining: Math.ceil(remainingMs / (24 * 60 * 60 * 1000)),
    nextChangeAt: next,
  };
}

/** Un cambio de peso cada 30 dias (RF-CAT-07). */
export function weightChangeCooldown(
  weightUpdatedAt: string | null,
  now: Date = new Date(),
): CooldownState {
  return cooldown(weightUpdatedAt, CATEGORY_RULES.WEIGHT_CHANGE_COOLDOWN_DAYS, now);
}

/** Un cambio de pais o ciudad cada 90 dias (RF-ONB-03). */
export function locationChangeCooldown(
  locationUpdatedAt: string | null,
  now: Date = new Date(),
): CooldownState {
  return cooldown(locationUpdatedAt, CATEGORY_RULES.LOCATION_CHANGE_COOLDOWN_DAYS, now);
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}
